import React, { useState, useEffect, useRef, useImperativeHandle, createElement } from 'react';
import classnames from 'classnames';
import { checkOddEvenIndex, getType, default_icon, useCachedIcons } from '../../utils';
import { ArrowDown } from '../../assets';
import './index.less';
export const TableCeil = (props) => {
    const { record, _index, level, isChild, indexWidth, anchorIndex, index, showIcon, highlight, icons, dataIndex, expandOpen, autoOpenClose, columns, onRowClick, rowStyle, } = props;
    const $index = dataIndex ? record[dataIndex] : _index;
    const [open, setOpen] = useState(expandOpen);
    useEffect(() => {
        if (autoOpenClose) {
            if (anchorIndex === _index && !open) {
                setOpen(true);
            }
            if (anchorIndex !== _index && open) {
                setOpen(false);
            }
        }
    }, [autoOpenClose, anchorIndex, record]);
    const renderIndex = (_i) => {
        if (!index) {
            return null;
        }
        const icon = icons?.[_i];
        const highlighted = highlight >= _i + 1;
        return (createElement("div", { className: "ceil", style: { width: indexWidth } }, isChild ? null : icon && showIcon && highlighted ? (createElement("span", null,
            createElement("img", { src: icon, width: "24px", height: "24px", alt: `rank-${_i + 1}` }))) : (createElement("span", { className: classnames('index', {
                highlighted: !!highlighted,
            }) }, _i + 1))));
    };
    const renderExpand = (col) => {
        const Node = (createElement(ArrowDown, { className: "arrow-down-icon", style: { transform: open ? 'rotate(180deg)' : undefined } }));
        return col?.expandExtra ? (col.expandExtra?.(Node, open)) : (createElement(React.Fragment, null,
            open ? `收起` : `展开`,
            Node));
    };
    return (createElement(React.Fragment, null,
        createElement("div", { className: "dv-scroll-row-item", onClick: () => onRowClick(record, _index, level), style: {
                ...rowStyle(record, _index, level),
            } },
            renderIndex($index),
            columns?.map((col, _i) => {
                const value = isChild
                    ? record?.[col?.expandDataIndex || col?.dataIndex]
                    : record?.[col?.dataIndex];
                return (createElement("div", { key: _i, className: classnames('ceil', {
                        [`align-${col.align}`]: !!col.align,
                    }) },
                    col?.render ? col.render?.(value, record, _index, level) : value,
                    record?.children?.length && col?.expandDataIndex ? (createElement("span", { className: "arrow-down", onClick: (e) => {
                            e.stopPropagation?.();
                            setOpen(!open);
                        } }, renderExpand(col))) : null));
            })),
        record?.children?.length && open
            ? record.children.map((child) => (createElement(TableCeil, { ...props, record: child, level: level + 1, isChild: true })))
            : null));
};
const ScrollTableTop = React.forwardRef((props, ref) => {
    const contentRef = useRef(null);
    const timeoutRef = useRef(null);
    const mouseRef = useRef(true);
    const [anchorIndex, setAnchorIndex] = useState(0);
    const [topDataSource, setTopDataSource] = useState([]);
    const { index, dataIndex, scrollConfig, indexHeader, columns, showHeader, headerBGC, headerColor, dataSource, topNumber = 5, model = `center`, expandOpen = true, autoOpenClose = true, size, indexWidth, showIcon, oddRowBGC, evenRowBGC, rowColor, style, icons = default_icon, highlight, onRowClick, rowSelectStyle, rowStyle, } = props;
    const level = 1;
    const cachedIcons = useCachedIcons(icons);
    const { autoplay = true, step = 1, autoplaySpeed = 3000 } = scrollConfig || {};
    const defaultIndexWidth = typeof indexWidth === 'number' ? `${indexWidth}px` : indexWidth || '50px';
    const gridTemplateColumns = React.useMemo(() => {
        const columnWidths = columns
            .map((item) => (typeof item.width === 'number' ? `${item.width}px` : item.width || '1fr'))
            .join(' ');
        return index ? `${defaultIndexWidth} ${columnWidths}` : columnWidths;
    }, [columns, index, defaultIndexWidth]);
    const getAnchorIndex = React.useCallback((source = []) => {
        const data = [...source].splice(0, topNumber) || [];
        if (model === 'default') {
            return 0;
        }
        if (model === 'center') {
            return Math.floor((data.length > 0 ? data.length : topNumber) / 2);
        }
    }, [topNumber, model]);
    const getDataSource = React.useCallback((source = []) => {
        const data = [...source].splice(0, topNumber) || [];
        if (data.length === 0)
            return data;
        if (model === 'default') {
            return data;
        }
        if (model === 'center') {
            const anchorIdx = getAnchorIndex(data);
            const arr = data.splice(anchorIdx + 1);
            return [...arr, ...data];
        }
    }, [topNumber, model, getAnchorIndex]);
    const handleClearTimeout = React.useCallback(() => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const handleAutoScroll = React.useCallback(() => {
        if (!autoplay || !mouseRef.current || !topDataSource.length) {
            return;
        }
        timeoutRef.current = window.setTimeout(() => {
            setTopDataSource((prevData) => {
                const newData = [...prevData];
                const arr = newData.splice(0, step);
                newData.push(...arr);
                const row = newData[anchorIndex] || {};
                onRowClick?.(row, anchorIndex, level);
                return newData;
            });
        }, autoplaySpeed);
    }, [autoplay, topDataSource, step, autoplaySpeed, anchorIndex, onRowClick]);
    useEffect(() => {
        const source = getType(dataSource) === 'array' ? dataSource : [];
        setAnchorIndex(getAnchorIndex(source));
        setTopDataSource(getDataSource(source));
    }, [dataSource, getAnchorIndex, getDataSource]);
    useEffect(() => {
        handleAutoScroll();
        return () => {
            handleClearTimeout();
        };
    }, [topDataSource, handleAutoScroll, handleClearTimeout]);
    useEffect(() => {
        return () => {
            handleClearTimeout();
        };
    }, []);
    const setSelectedRow = (_index) => {
        if (_index === anchorIndex)
            return;
        setTopDataSource((prevData) => {
            const newData = [...prevData];
            if (model === 'default') {
                const arr = newData.splice(0, _index - anchorIndex);
                newData.push(...arr);
            }
            if (model === 'center') {
                if (_index > anchorIndex) {
                    const arr = newData.splice(0, _index - anchorIndex);
                    newData.push(...arr);
                }
                else {
                    const arr = newData.splice(_index + anchorIndex + 1);
                    newData.unshift(...arr);
                }
            }
            return newData;
        });
    };
    const handleRowClick = (record, _index, _level) => {
        onRowClick?.(record, _index, _level);
        setSelectedRow(_index);
    };
    useImperativeHandle(ref, () => ({ setSelectedRow }));
    const renderHeader = () => {
        if (!showHeader) {
            return null;
        }
        const headStyle = {
            backgroundColor: headerBGC,
            color: headerColor,
        };
        return (createElement("div", { className: "header", style: {
                gridTemplateColumns,
                ...headStyle,
            } },
            index && createElement("div", { className: "header-item" }, indexHeader || '#'),
            columns?.map((col, i) => (createElement("div", { key: col?.key || col?.dataIndex || i, className: classnames('header-item', {
                    [`align-${col.align}`]: !!col.align,
                }) }, col.title)))));
    };
    const renderRowStyle = (record, _index, _level) => {
        const rowStyleDefault = getType(rowStyle) === 'function' ? rowStyle(record, _index, _level) : rowStyle;
        let selectStyle = {};
        if (anchorIndex === _index) {
            selectStyle =
                getType(rowSelectStyle) === 'function'
                    ? rowSelectStyle(record, _index, _level)
                    : rowSelectStyle;
        }
        return {
            gridTemplateColumns,
            background: checkOddEvenIndex(_index + 1) ? evenRowBGC : oddRowBGC,
            color: rowColor,
            ...rowStyleDefault,
            ...selectStyle,
        };
    };
    const renderContent = () => {
        return (createElement("div", { className: "dv-scroll-rows-content", ref: contentRef }, topDataSource.map((record, _index) => (createElement(React.Fragment, { key: `row-${_index.toString()}` },
            createElement(TableCeil, { record: record, _index: _index, level: level, index: index, anchorIndex: anchorIndex, indexWidth: defaultIndexWidth, showIcon: showIcon, highlight: highlight, icons: cachedIcons, dataIndex: dataIndex, expandOpen: expandOpen, autoOpenClose: autoOpenClose, columns: columns, onRowClick: handleRowClick, rowStyle: renderRowStyle }))))));
    };
    return (createElement("div", { className: classnames('dv-scroll-table-top', {
            [size]: !!size,
        }), style: style },
        renderHeader(),
        createElement("div", { className: "dv-scroll-rows", onMouseEnter: () => {
                mouseRef.current = false;
                handleClearTimeout();
            }, onMouseLeave: () => {
                mouseRef.current = true;
                handleAutoScroll();
            } }, renderContent())));
});
export default class DataVScrollTableTop extends React.Component {
    ref;
    constructor(props) {
        super(props);
        this.ref = React.createRef();
    }
    render() {
        return createElement(ScrollTableTop, { ...this.props, ref: this.ref });
    }
}
